import config from 'config'
import fetch from 'isomorphic-fetch'
import qs from 'qs'
import { processURLAddress } from '@vue-storefront/core/helpers'
import rootStore from '@vue-storefront/core/store'

const getEndpoint = (): string => {
  return (config.netsuite && config.netsuite.endpoint) || 'https://lbpprodata.luxbp.com'
}

export const getAuthToken = (): string | null => {
  return rootStore.getters['user/getUserToken'] || null
}

const buildUrl = (path: string, query?: Record<string, any>): string => {
  let url = `${processURLAddress(getEndpoint())}${path}`
  if (query && Object.keys(query).length) {
    url += `?${qs.stringify(query, { arrayFormat: 'comma' })}`
  }
  return url
}

const authHeaders = (extra: Record<string, string> = {}): Record<string, string> => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('Not authenticated')
  }
  return {
    ...extra,
    Authorization: `Bearer ${token}`
  }
}

export const netsuiteFetch = async (
  path: string,
  options: RequestInit = {},
  query?: Record<string, any>
): Promise<any> => {
  const response = await fetch(buildUrl(path, query), {
    ...options,
    mode: 'cors',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders((options.headers as Record<string, string>) || {})
    }
  })

  const json = await response.json().catch(() => ({}))

  if (!response.ok) {
    const message = (json && json.error) || response.statusText || 'Request failed'
    const error: any = new Error(message)
    error.status = response.status
    error.body = json
    throw error
  }

  return json
}

/** Fetch a non-JSON response (e.g. CSV download). */
export const netsuiteFetchBlob = async (
  path: string,
  options: RequestInit = {},
  query?: Record<string, any>
): Promise<{ blob: Blob, filename: string | null }> => {
  const response = await fetch(buildUrl(path, query), {
    ...options,
    mode: 'cors',
    credentials: 'omit',
    headers: {
      Accept: 'text/csv, application/json;q=0.9, */*;q=0.8',
      ...authHeaders((options.headers as Record<string, string>) || {})
    }
  })

  if (!response.ok) {
    const json = await response.json().catch(() => ({}))
    const message = (json && json.error) || response.statusText || 'Request failed'
    const error: any = new Error(message)
    error.status = response.status
    error.body = json
    throw error
  }

  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)"?/i)
  const filename = match ? decodeURIComponent(match[1]) : null
  const blob = await response.blob()

  return { blob, filename }
}
